import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {MicroLabel} from '@/components/period-selector'
import {WorkerCountDialog} from '@/components/worker-count-dialog'
import {useUpgradeGate} from '@/components/billing/use-upgrade-gate'
import {CheckGlyph} from '@/components/ui/icons'
import {chapterNoteFor} from './chapter-note'
import {CHAPTER_STANDARD_COUNTS, useRederiveAssessment, type SgsstState} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {BAND_TONE} from '@/lib/bands'
import {formatDate, formatRatePct} from '@/lib/risk'
import {translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'

const CYCLE_BAR: Record<string, string> = {
  PLANEAR: 'bg-chart-planear',
  HACER: 'bg-chart-hacer',
  VERIFICAR: 'bg-chart-verificar',
  ACTUAR: 'bg-chart-actuar',
}

const CYCLE_ORDER = ['PLANEAR', 'HACER', 'VERIFICAR', 'ACTUAR']

function HeaderFact({label, tip, children}: {label: string; tip?: React.ReactNode; children: React.ReactNode}) {
  return (
    <div className="min-w-0">
      <p className="flex items-center gap-1 font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        {label}
        {tip}
      </p>
      <div className="mt-1 font-mono text-[13px] text-foreground">{children}</div>
    </div>
  )
}

function Meter({
  label,
  earned,
  possible,
  fill,
  labelClassName,
}: {
  label: string
  earned: number
  possible: number
  fill: string
  labelClassName?: string
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <MicroLabel className={cn('truncate text-foreground', labelClassName)}>{label}</MicroLabel>
        <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
          {earned.toLocaleString('es-CO')} / {possible.toLocaleString('es-CO')}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-track">
        <div
          className={cn('h-full rounded-full transition-all', fill)}
          style={{width: `${possible ? (earned / possible) * 100 : 0}%`}}
        />
      </div>
    </div>
  )
}

export function ScoreCard({
  state,
  fase2,
  businessId,
  canEditWorkers,
}: {
  state: SgsstState
  fase2: boolean
  businessId: string
  canEditWorkers: boolean
}) {
  const {t} = useTranslation()
  const assessment = state.assessment!
  const score = assessment.score
  const actions = t(`sgsst.bands.${score.band}.actions`, {returnObjects: true}) as string[]
  const rederive = useRederiveAssessment(businessId)
  const [editingWorkers, setEditingWorkers] = useState(false)
  const [workerError, setWorkerError] = useState<string | null>(null)
  const upgradeGate = useUpgradeGate(businessId, 'conteo', (band) =>
    setWorkerError(t('planFacturacion.upgradeModal.retryNote', {band}))
  )
  // Saving re-pins the chapter, so warn when the new count would cross a threshold.
  const chapterNote = chapterNoteFor(state.risk_class, assessment.chapter, t)

  const workersLabel = t('sgsst.workers', {count: assessment.worker_count})

  return (
    <section className="surface" data-testid="nav-plan">
      <div className="surface-divider flex flex-wrap items-start justify-between gap-x-6 gap-y-4 border-b px-5 py-4">
        <div className="min-w-0">
          <MicroLabel>{t('sgsst.score.eyebrow')}</MicroLabel>
          {fase2 ? (
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-4" data-testid="dual-score">
              <div>
                <MicroLabel>{t('sgsst.dual.frozenLabel')}</MicroLabel>
                <div className="mt-1 flex items-center gap-3">
                  <span className="font-heading text-3xl text-foreground">
                    {formatRatePct(assessment.snapshot_pct ?? 0)}
                  </span>
                  <Tag tone={BAND_TONE[assessment.snapshot_band!]}>
                    {t(`sgsst.bands.${assessment.snapshot_band!}.label`)}
                  </Tag>
                </div>
                {/* Check, no candado: el candado de la casa dice «falta un paso». */}
                <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CheckGlyph />
                  <span className="font-mono">
                    {t('sgsst.dual.registeredOn', {date: formatDate(assessment.autoevaluacion_completed_at!)})}
                  </span>
                </p>
              </div>
              <div className="border-l border-border pl-6">
                <MicroLabel>{t('sgsst.dual.liveLabel')}</MicroLabel>
                <div className="mt-1">
                  <span className="font-heading text-3xl text-primary">{formatRatePct(score.total_pct)}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{t('sgsst.dual.liveCaption')}</p>
              </div>
            </div>
          ) : (
            <>
              <MicroLabel className="mt-1.5 text-muted-foreground/80">{t('sgsst.dual.inProgressLabel')}</MicroLabel>
              <div className="mt-1 flex items-center gap-3">
                <span className="font-heading text-3xl text-primary">{formatRatePct(score.total_pct)}</span>
                <Tag tone={BAND_TONE[score.band]}>{t(`sgsst.bands.${score.band}.label`)}</Tag>
                <HelpIcon label={t('sgsst.score.helpLabel')} testId="score-help">
                  <div className="space-y-2">
                    {score.resolved === 0 && score.auto_credit_pct > 0 && (
                      <p data-testid="starting-score-note">{t('sgsst.score.startingHelp')}</p>
                    )}
                    {score.auto_credit_pct > 0 && (
                      <p>{t('sgsst.score.autoCredit', {pct: formatRatePct(score.auto_credit_pct)})}</p>
                    )}
                    <ul className="list-disc space-y-1 pl-4">
                      {actions.map((action) => (
                        <li key={action}>{action}</li>
                      ))}
                    </ul>
                  </div>
                </HelpIcon>
              </div>
            </>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            {t('sgsst.score.resolved', {resolved: score.resolved, workable: score.workable})}
          </p>
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <HeaderFact
              label={t('sgsst.score.chapterLabel')}
              tip={
                <HelpIcon label={t('sgsst.score.chapterHelpLabel')} testId="sgsst-chapter-help">
                  {t('sgsst.score.chapterHelp')}
                </HelpIcon>
              }>
              {t('sgsst.chapter', {chapter: assessment.chapter})}
            </HeaderFact>
            <HeaderFact label={t('sgsst.score.standardsLabel')}>
              {t('sgsst.standardsCount', {count: CHAPTER_STANDARD_COUNTS[assessment.chapter]})}
            </HeaderFact>
            <HeaderFact
              label={t('sgsst.score.workersLabel')}
              tip={
                fase2 ? (
                  <HelpIcon label={t('sgsst.score.workersFrozenLabel')} testId="workers-frozen-note">
                    {t('sgsst.workersFrozen', {
                      date: formatDate(assessment.autoevaluacion_completed_at!),
                      next: assessment.period_year + 1,
                    })}
                  </HelpIcon>
                ) : null
              }>
              {canEditWorkers ? (
                <button
                  type="button"
                  data-testid="edit-workers"
                  onClick={() => {
                    setWorkerError(null)
                    setEditingWorkers(true)
                  }}
                  className="underline decoration-dotted underline-offset-2 hover:text-primary">
                  {workersLabel}
                </button>
              ) : (
                workersLabel
              )}
            </HeaderFact>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-5 gap-y-4 px-5 py-4 sm:grid-cols-4">
        {CYCLE_ORDER.map((cycle) => {
          const progress = score.cycles.find((c) => c.cycle === cycle)!
          return (
            <Meter
              key={cycle}
              label={cycle}
              earned={progress.earned}
              possible={progress.possible}
              fill={CYCLE_BAR[cycle]}
            />
          )
        })}
      </div>
      <details className="surface-divider border-t">
        <summary
          className="cursor-pointer px-5 py-2.5 text-xs text-muted-foreground hover:text-foreground"
          data-testid="score-by-group-toggle">
          {t('sgsst.score.byGroup')}
        </summary>
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 px-5 pb-4 sm:grid-cols-4" data-testid="score-by-group">
          {score.groups.map((group) => (
            <Meter
              key={group.group_name}
              label={group.group_name}
              earned={group.earned}
              possible={group.possible}
              fill="bg-primary"
            />
          ))}
        </div>
      </details>
      <WorkerCountDialog
        open={editingWorkers}
        onOpenChange={setEditingWorkers}
        title={t('workerCount.sgsstDialogTitle')}
        description={t('workerCount.sgsstDialogBody')}
        initial={assessment.worker_count}
        pending={rederive.isPending}
        error={workerError}
        note={chapterNote}
        onSave={(count) =>
          rederive.mutate(count, {
            onSuccess: () => setEditingWorkers(false),
            onError: (err) => {
              if (upgradeGate.catchGate(err, {proposed_declared_worker_count: count})) {
                setEditingWorkers(false)
                return
              }
              setWorkerError(translateApiError(err))
            },
          })
        }
      />
      {upgradeGate.modal}
    </section>
  )
}
